import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { selectAccessLevel } from '../../../../slices/subscriptions/subscriptionsSlice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Minus, X } from '@phosphor-icons/react';
import { AppDispatch, RootState } from '../../../../store/store';
import {
  dismissDiscount,
  loadActiveDiscount,
  selectActiveDiscount,
  selectDismissedDiscountSourceName,
} from '../../../../slices/discount/discountSlice';
import _ from 'lodash';
import { Capacitor } from '@capacitor/core';
import { withPosthogAfterIdle } from '../../../../utils/posthogClient';
import { clearDiscountTracking } from '../../../../utils/attribution';
import {
  readDismissedDiscountSource,
  writeDismissedDiscountSource,
} from '../../../../utils/discountDismissal';
import {
  isTipDismissed,
  recordTipDismissed,
} from '../../../../helpers/messageStateGating';
import type { MessageKey } from '../../../../config/messageRegistry';
import {
  formatTrialPeriod,
  getSharedExplicitTrialPeriodDays,
} from '../../../../utils/pricing';
import {
  DISCOUNT_COLLAPSE_DURATION_MS,
  readDiscountCollapsedUntil,
  writeDiscountCollapsedUntil,
} from '../../../../utils/discountCollapse';
import { getSchoolLaunchDeadline } from '../../../../utils/discountExpiry';
import { useDiscountCountdownVariant } from '../../../../utils/discountCountdownExperiment';
import DiscountCountdownChip from '../DiscountCountdown/DiscountCountdownChip';

// The dismiss control has two modes. 'collapse' (current) hides the banner for
// an hour via the minus button, then it re-appears; 'dismiss' is the legacy
// permanent X dismissal. Both code paths are kept live so we can revert by
// flipping this single constant — the type annotation keeps the unused-branch
// comparisons valid under TS.
const DISMISS_MODE: 'collapse' | 'dismiss' = 'collapse';
// Fired after a collapse is written so every useDiscountBannerVisibility
// instance (shell layout reserve + banner component) re-reads the marker.
const DISCOUNT_COLLAPSED_EVENT = 'ora-discount-collapsed';

// Per-instance dismissal keys (ORA-188): one message-state row per stable
// discount occurrence id, so a NEW promo — or a re-run of an offer with a new
// window, or the next referral bonus — re-shows even after a different one was
// dismissed. Gated on instanceId being present (absent on older backends →
// source-name path, the pre-ORA-188 behavior). Shared by the visibility hook
// and the component's dismiss handler.
const useDiscountDismissalKeys = () => {
  const activeDiscount = useSelector(selectActiveDiscount);
  const discountInstanceId = activeDiscount?.source?.instanceId ?? null;

  const discountMessageKey: MessageKey | null = discountInstanceId
    ? `discount-dismissed:${discountInstanceId}`
    : null;

  // The durable local marker is keyed per-occurrence when the backend supplies
  // a stable instanceId, so dismissing one discount can't suppress a later,
  // distinct one. Older backends omit instanceId → key on the source name.
  const localDismissalToken =
    discountInstanceId ??
    (activeDiscount?.source?.name || 'discount').toLowerCase();

  return { discountMessageKey, localDismissalToken };
};

// Shared visibility logic so the OS shell can reserve space for the banner
// (and render it once at shell level) with exactly the same rules the
// component itself uses.
export const useDiscountBannerVisibility = (): boolean => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const accessLevel = useSelector(selectAccessLevel);
  const activeDiscount = useSelector(selectActiveDiscount);
  const dismissedSourceName = useSelector(selectDismissedDiscountSourceName);

  const { discountMessageKey, localDismissalToken } =
    useDiscountDismissalKeys();

  // Temporary collapse (the minus control): the banner hides until this epoch-ms
  // timestamp, then re-appears. The deadline is tagged with the user id it
  // belongs to so an account switch can't apply the prior user's collapse for a
  // paint before the effect below resets it. Read from the durable per-user key
  // once we know the user id; a timer re-shows it within the session when the
  // window elapses. The hook runs in two places (the shell's layout reserve and
  // the banner component), so the component announces a fresh collapse via a
  // window event and every instance re-reads the marker.
  // Seeded synchronously when the user id is already known so a collapsed
  // banner never counts as visible for its first render (the countdown
  // experiment reads its flag off `isVisible`, and that read is an exposure).
  const [collapse, setCollapse] = useState<{
    userId: string;
    until: number;
  } | null>(() => {
    const userId = currentUser?.id;
    const until = readDiscountCollapsedUntil(userId);
    return userId && until != null ? { userId, until } : null;
  });

  useEffect(() => {
    const sync = () => {
      const userId = currentUser?.id;
      const until = readDiscountCollapsedUntil(userId);
      setCollapse(userId && until != null ? { userId, until } : null);
    };

    sync();
    window.addEventListener(DISCOUNT_COLLAPSED_EVENT, sync);
    return () => window.removeEventListener(DISCOUNT_COLLAPSED_EVENT, sync);
  }, [currentUser?.id]);

  useEffect(() => {
    if (collapse == null) return;
    const remaining = collapse.until - Date.now();
    if (remaining <= 0) {
      setCollapse(null);
      return;
    }
    const timer = setTimeout(() => setCollapse(null), remaining);
    return () => clearTimeout(timer);
  }, [collapse]);

  // Only hides the banner in 'collapse' mode and only for the current user, so
  // reverting to 'dismiss' mode ignores any leftover marker and an account
  // switch can't hide the new user's banner. A past timestamp never hides it,
  // even before the clearing effect above runs.
  const isCollapsed =
    DISMISS_MODE === 'collapse' &&
    collapse != null &&
    collapse.userId === currentUser?.id &&
    collapse.until > Date.now();

  // The dismissal is durably stored per-user in its own localStorage key; the
  // redux `dismissedSourceName` provides same-session reactivity. Read both so
  // the banner stays hidden after a reload even when the redux-persist blob
  // failed to save (mobile quota/eviction), and without a first-paint flash.
  const effectiveDismissedSourceName = useMemo(
    () => readDismissedDiscountSource(currentUser?.id) ?? dismissedSourceName,
    [currentUser?.id, dismissedSourceName]
  );

  const isDismissedInStore = useSelector((state: RootState) =>
    discountMessageKey
      ? isTipDismissed(state, state.user.profile, discountMessageKey)
      : false
  );

  // Dual-read: the normalized store (authoritative, per-instance, cross-device)
  // OR the durable local marker for the current occurrence.
  const isDismissed =
    isDismissedInStore ||
    (Boolean(effectiveDismissedSourceName) &&
      effectiveDismissedSourceName === localDismissalToken);

  return (
    Boolean(activeDiscount) &&
    !(Capacitor.getPlatform() === 'android') &&
    (accessLevel === 'FREE' || accessLevel === 'TRIAL') &&
    !isDismissed &&
    !isCollapsed
  );
};

const DiscountBanner: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const activeDiscount = useSelector(selectActiveDiscount);
  const isVisible = useDiscountBannerVisibility();

  // Discount countdown experiment (ORA-892): a School Launch discount with a
  // future phase deadline is the only kind that gets a countdown, and the
  // variant is read only while the banner is actually on screen for an authed
  // user, so the exposure matches the surface. When the deadline passes the
  // discount is refetched: the backend then returns the step-down phase (with
  // its own deadline) or no discount at all, and the banner follows.
  const countdownDeadline = useMemo(
    () => getSchoolLaunchDeadline(activeDiscount),
    [activeDiscount]
  );

  const countdownVariant = useDiscountCountdownVariant({
    userId: currentUser?.id,
    enabled: isVisible && countdownDeadline !== null,
  });

  const showCountdown =
    countdownVariant === 'test' && countdownDeadline !== null;

  const handleCountdownExpired = useCallback(() => {
    void dispatch(loadActiveDiscount());
  }, [dispatch]);

  const { discountMessageKey, localDismissalToken } =
    useDiscountDismissalKeys();

  const handleDismiss = (event: React.MouseEvent | React.KeyboardEvent) => {
    // Stop the click from following the parent <Link> to /pricing.
    event.preventDefault();
    event.stopPropagation();
    // Normalized store (authoritative, per-instance, cross-device).
    if (discountMessageKey) {
      void recordTipDismissed(discountMessageKey);
    }
    // Same-session redux + a dedicated durable key, both keyed per-occurrence
    // (the instanceId when present) so the dismissal survives reloads even if the
    // shared redux-persist blob fails to write (mobile quota/eviction), without
    // suppressing a later, distinct discount.
    dispatch(dismissDiscount(localDismissalToken));
    writeDismissedDiscountSource(currentUser?.id, localDismissalToken);
    // Also clear the shared .oraai.com tracking cookies so the banner stops
    // showing on the marketing site too.
    clearDiscountTracking();
  };

  // Temporary collapse: hide the banner for the collapse window and persist the
  // deadline so a reload keeps it collapsed until the hour is up. The event
  // tells every visibility-hook instance (incl. the shell's) to re-read it.
  const handleCollapse = (event: React.MouseEvent | React.KeyboardEvent) => {
    // Stop the click from following the parent <Link> to /pricing.
    event.preventDefault();
    event.stopPropagation();
    const until = Date.now() + DISCOUNT_COLLAPSE_DURATION_MS;
    writeDiscountCollapsedUntil(currentUser?.id, until);
    window.dispatchEvent(new Event(DISCOUNT_COLLAPSED_EVENT));
  };

  // The active control: minus/collapse by default, X/dismiss when reverted.
  // Both handlers stay referenced so neither code path goes dead.
  const handleControl =
    DISMISS_MODE === 'collapse' ? handleCollapse : handleDismiss;

  const handleControlKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleControl(event);
    }
  };

  const expirationDate = activeDiscount?.discount?.expiresAt
    ? moment(activeDiscount.discount.expiresAt)
    : null;

  const isSketchyDiscount =
    activeDiscount?.source?.name?.toLowerCase().includes('sketchy') === true;

  const isMdplusDiscount =
    activeDiscount?.source?.scope === 'marketing_offer' &&
    activeDiscount?.source?.name?.toLowerCase() === 'mdplus';

  const isMarketingOfferDiscount =
    activeDiscount?.source?.scope === 'marketing_offer';

  // Only offers whose payload explicitly overrides the trial (partner offers)
  // get the trial-forward copy below; every other marketing offer keeps its
  // pre-existing percent / amount banner.
  const marketingOfferTrialPeriodDays =
    getSharedExplicitTrialPeriodDays(activeDiscount);

  const isEarlybirdDiscount =
    activeDiscount?.source?.name?.toLowerCase() === 'earlybird 2026';

  const isReferralDiscount = activeDiscount?.source?.scope === 'referral';
  const sourceName = activeDiscount?.source?.name || 'discount';

  const renderSourceSuffix = () => {
    if (expirationDate) {
      return `${sourceName} expires ${moment(expirationDate).fromNow()} — Claim now!`;
    }

    return `${sourceName} is live now — Claim now!`;
  };

  const renderUnlockedText = (claimText: string) => {
    if (isMarketingOfferDiscount) {
      return `${sourceName} unlocked! ${claimText}`;
    }

    if (expirationDate) {
      return `${sourceName} unlocked! Expires in ${moment(
        expirationDate
      ).fromNow(true)}. ${claimText}`;
    }

    return `${sourceName} unlocked! ${claimText}`;
  };

  useEffect(() => {
    if (!isVisible || !currentUser?.id || !activeDiscount) return;

    const sourceName = activeDiscount.source?.name || 'unknown';
    const sessionKey = `ora-discount-banner-shown:${currentUser.id}:${sourceName}`;
    if (sessionStorage.getItem(sessionKey)) return;

    const cancelPosthog = withPosthogAfterIdle((posthog) => {
      posthog.capture('discount_banner_shown', {
        discount_source_name: sourceName,
        discount_source_scope: activeDiscount.source?.scope || '',
        partner_source: currentUser.attributionSource || '',
        utm_source: currentUser.attributionSource || '',
        utm_campaign: currentUser.attributionCampaign || '',
        discount_has_expiration: Boolean(activeDiscount.discount?.expiresAt),
        discount_has_percent_off: !_.isNil(activeDiscount.discount?.percentOff),
        discount_has_amount_off: !_.isNil(activeDiscount.discount?.amountOff),
      });
    });

    sessionStorage.setItem(sessionKey, '1');
    return cancelPosthog;
  }, [isVisible, currentUser?.id, activeDiscount]);

  const renderDiscountText = () => {
    if (isReferralDiscount) {
      return <p>One Month Free - Claim Your Referral Bonus!</p>;
    }

    if (isMdplusDiscount) {
      return (
        <p>
          MDplus deal: 3 free months of Ora AI!{' '}
          {expirationDate
            ? `Expires in ${moment(expirationDate).fromNow(true)}.`
            : 'Claim now!'}
        </p>
      );
    }

    if (isSketchyDiscount) {
      return (
        <p>Welcome to Ora! Claim Your 2 Month Sketchy X Ora Discount Now!</p>
      );
    }

    if (isMarketingOfferDiscount && marketingOfferTrialPeriodDays != null) {
      return (
        <p>
          {formatTrialPeriod(marketingOfferTrialPeriodDays, 'banner')}
          {!_.isNil(activeDiscount?.source?.displayPercentOff) && (
            <> + {activeDiscount.source.displayPercentOff}% off</>
          )}{' '}
          — from {sourceName}. Claim now!
        </p>
      );
    }

    // Campaign-level headline percent (ORA-44 universal / ORA-45 special). The
    // per-plan amounts differ, so we advertise the marketing percent, not a
    // single dollar figure. Keyed on displayPercentOff so future campaigns need
    // no banner change. Placed after the bespoke campaigns above so it only
    // catches discounts that opt in by setting displayPercentOff.
    if (!_.isNil(activeDiscount?.source?.displayPercentOff)) {
      return (
        <p>
          {sourceName} —{' '}
          {marketingOfferTrialPeriodDays != null && (
            <>{formatTrialPeriod(marketingOfferTrialPeriodDays, 'banner')} + </>
          )}
          {activeDiscount?.source?.displayPercentOff}% off Ora Pro! Claim now!
        </p>
      );
    }

    if (!_.isNil(activeDiscount?.discount?.percentOff)) {
      return (
        <p>
          {activeDiscount?.discount?.percentOff}% off for{' '}
          {activeDiscount?.discount?.durationMonths} months!{' '}
          {renderSourceSuffix()}
        </p>
      );
    }

    if (!_.isNil(activeDiscount?.discount?.amountOff)) {
      if (activeDiscount?.discount?.amountOff >= 20) {
        if (isEarlybirdDiscount) {
          return <p>{renderUnlockedText('Claim 50% off an annual plan →')}</p>;
        }

        if (
          activeDiscount?.discount?.durationMonths &&
          activeDiscount?.discount?.durationMonths > 1
        ) {
          return (
            <p>
              {renderUnlockedText(
                `Claim your ${activeDiscount.discount.durationMonths} free months now →`
              )}
            </p>
          );
        }

        return <p>{renderUnlockedText('Claim your first free month now →')}</p>;
      }

      return (
        <p>
          ${activeDiscount?.discount?.amountOff} off any plan!{' '}
          {renderSourceSuffix()}
        </p>
      );
    }

    return null;
  };

  if (isVisible) {
    return (
      <Link to="/pricing" className="discount-container mb-6">
        {showCountdown ? (
          // The shell strip has a fixed height, so the chip and the copy share
          // one line: the copy truncates, the chip never shrinks or wraps.
          <div className="discount-header flex min-w-0 items-center justify-center gap-x-2">
            <div className="min-w-0 truncate">{renderDiscountText()}</div>
            <DiscountCountdownChip
              className="shrink-0"
              deadline={countdownDeadline}
              onExpired={handleCountdownExpired}
            />
          </div>
        ) : (
          <div className="discount-header">{renderDiscountText()}</div>
        )}
        <span
          role="button"
          tabIndex={0}
          aria-label={
            DISMISS_MODE === 'collapse'
              ? 'Collapse discount banner'
              : 'Dismiss discount banner'
          }
          className="discount-dismiss"
          onClick={handleControl}
          onKeyDown={handleControlKeyDown}
        >
          {DISMISS_MODE === 'collapse' ? (
            <Minus weight="bold" />
          ) : (
            <X weight="bold" />
          )}
        </span>
      </Link>
    );
  }

  return <></>;
};

export default DiscountBanner;
